import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'context/ThemeContext';
import { HeroSlide } from '../components/HeroSlide/HeroSlide';
import { useGenerateSlides } from '../hooks/useGenerateSlides';

const SLIDE_INTERVAL_MS = 8000;

function getDotClass(index: number, activeIndex: number): string {
  const distance = Math.abs(index - activeIndex);
  if (distance === 0) return '';
  if (distance === 1) return 'opacity-40';
  return 'opacity-20';
}

export function HeroSlideshow() {
  const { t } = useTranslation('home');
  const { theme } = useTheme();
  const slides = useGenerateSlides()

  const [activeIndex, setActiveIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    setVisible(true);
  }, []);

  const goToSlide = useCallback((index: number) => {
    setVisible(false);
    setTimeout(() => {
      setActiveIndex(index);
      setVisible(true);
    }, 1000);
  }, []);

  useEffect(() => {
    if (slides.length <= 1 || isPaused) return;
    const timer = setInterval(() => {
      goToSlide((activeIndex + 1) % slides.length);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [activeIndex, slides.length, goToSlide, isPaused]);

  if (slides.length === 0) return null;

  const { slide, badgeLabel } = slides[activeIndex];

  const hasSlides = slides.length > 1

  return (
    <section
      aria-roledescription="carousel"
      aria-label={t('hero.ariaLabel')}
      className="relative flex items-start pt-32 pb-16 lg:pt-48 lg:pb-40 overflow-hidden -mt-24"
      onFocus={() => setIsPaused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setIsPaused(false);
      }}
    >
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-surface opacity-40 z-[5]" />
        <div className="absolute inset-0 bg-gradient-to-r from-surface via-surface/80 to-transparent z-10" />
        <img
          src={theme === 'light' ? '/images/backgrounds/home-hero-bg-light.png' : '/images/backgrounds/home-hero-bg.jpg'}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      <div className="relative z-20 max-w-7xl mx-auto px-6 lg:px-12 w-full lg:grid lg:grid-cols-[1fr_24rem] lg:gap-8 lg:items-center">
        <div
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="flex items-center gap-4 mb-6">
            <span
              className="min-w-[10rem] bg-tertiary/20 text-tertiary px-3 py-1 rounded-full text-xs font-sans uppercase tracking-widest font-bold transition-opacity duration-1000"
              style={{ opacity: visible ? 1 : 0, transitionTimingFunction: visible ? 'ease-in' : 'ease-out' }}
            >
              {badgeLabel}
            </span>
            {hasSlides && (
              <div className="flex gap-2">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goToSlide(i)}
                    aria-label={t('hero.goToSlide', { number: i + 1 })}
                    className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 bg-tertiary-fixed-dim ${getDotClass(i, activeIndex)}`}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="grid">
            {slides.map(({ slide: s }, i) => {
              const isActive = i === activeIndex;
              return (
                <div
                  key={i}
                  className={`col-start-1 row-start-1 ${isActive ? '' : 'pointer-events-none'}`}
                  role="group"
                  aria-roledescription="slide"
                  aria-label={t('hero.slideOf', { current: i + 1, total: slides.length })}
                  onFocus={() => { if (!isActive) setActiveIndex(i); }}
                >
                  <HeroSlide slide={s} visible={isActive && visible} />
                </div>
              );
            })}
          </div>
        </div>

        <img
          src={slide.image?.src}
          alt={slide.image?.alt ?? ''}
          className="hidden lg:block w-full h-[32rem] rounded-t-full overflow-hidden shadow-2xl bg-surface-container-high transition-opacity duration-[1.5s] object-cover justify-self-end"
          style={{
            opacity: visible ? 1 : 0,
            transitionTimingFunction: visible ? 'ease-in' : 'ease-out'
          }}
        />
      </div>

      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {t('hero.slideAnnouncement', { current: activeIndex + 1, total: slides.length, title: slide.title })}
      </div>
    </section>
  );
}
